import {
  IGetResourceOverviewParameters,
  IGetResourceStatusParameters,
  IGetSuggestedResourcesParameters,
  Resource,
  ResourceCompressed,
  ResourceOverviewResponseDto,
  ResourceService,
  ResourceStateDto,
  ResourceTagCompressed,
} from '@jooxter/api';
import { useCallback } from 'react';
import { AxiosError } from 'axios';
import { keepPreviousData, useQueries, useQuery, UseQueryResult } from '@tanstack/react-query';
import { ResourceQueryKeys } from './queryKeys';
import { ResourceStaleTimeEnum } from './staleTimes';

const REFETCH_INTERVAL = 30000;

export const useFetchResource = (
  id?: number
): { resource: Resource | null | undefined; isLoading: boolean; error: AxiosError | null } => {
  const fetchResource = useCallback(() => {
    if (id) {
      return ResourceService.getById(id);
    }

    return Promise.resolve(null);
  }, [id]);

  const {
    data: resource,
    isLoading,
    error,
  } = useQuery<Resource | null, AxiosError, Resource | null>({
    queryKey: [ResourceQueryKeys.GetResource, id],
    queryFn: () => fetchResource(),
    placeholderData: keepPreviousData,
    staleTime: ResourceStaleTimeEnum.GetResource,
    enabled: !!id,
  });

  return { resource, isLoading, error };
};

export const useFetchResourceStatus = (
  options?: IGetResourceStatusParameters,
  enabled = true,
  refetchInterval?: number
): { status: ResourceStateDto | undefined; isLoading: boolean } => {
  const fetchResourceStatus = useCallback(() => {
    if (!options) {
      return Promise.resolve(undefined);
    }

    return ResourceService.getResourceStatus(options);
  }, [options]);

  const { data: status, isLoading } = useQuery({
    queryKey: [ResourceQueryKeys.GetResourceStatus, options],
    queryFn: () => fetchResourceStatus(),
    placeholderData: keepPreviousData,
    staleTime: ResourceStaleTimeEnum.GetResourceStatus,
    refetchInterval,
    enabled: enabled && !!options,
  });

  return { status, isLoading };
};

export const useFetchResourceOverview = (
  options: IGetResourceOverviewParameters,
  enabled = true
): { overview: ResourceOverviewResponseDto | undefined; isLoading: boolean } => {
  const fetchResource = useCallback(() => {
    return ResourceService.getOverview(options);
  }, [options]);

  const { data: overview, isLoading } = useQuery({
    queryKey: [ResourceQueryKeys.GetResourceOverview, options],
    queryFn: () => fetchResource(),
    refetchInterval: REFETCH_INTERVAL,
    placeholderData: keepPreviousData,
    staleTime: ResourceStaleTimeEnum.GetResourceOverview,
    enabled,
  });

  return { overview, isLoading };
};

/** The resources last booked by the current user; with `from` and `to`, only the free ones. */
export const useFetchSuggestedResources = (
  options?: IGetSuggestedResourcesParameters,
  enabled = true
): { resources: ResourceCompressed[] | undefined; isLoading: boolean } => {
  const { data: resources, isLoading } = useQuery({
    queryKey: [ResourceQueryKeys.GetSuggestedResources, options],
    queryFn: () => ResourceService.getSuggestedResources(options),
    placeholderData: keepPreviousData,
    staleTime: ResourceStaleTimeEnum.GetSuggestedResources,
    enabled,
  });

  return { resources, isLoading };
};

/** At module level: a stable `combine`, so its result is too while the tags don't change. */
const combineTags = (results: UseQueryResult<ResourceTagCompressed[]>[]) => ({
  tags: results.map((result) => result.data),
  isLoading: results.some((result) => result.isLoading),
});

/**
 * The tags (sensors, check-in tags…) of several resources, in the order of `resourceIds` — undefined
 * while one is loading. The same query, so the same cache, as `useFetchResourcesTags` (@jooxter/core):
 * the API has no search on several resources, one request each.
 */
export const useFetchTagsOfResources = (
  resourceIds: number[]
): { tags: (ResourceTagCompressed[] | undefined)[]; isLoading: boolean } =>
  useQueries({
    queries: resourceIds.map((resourceId) => ({
      queryKey: [ResourceQueryKeys.GetResourceTags, resourceId],
      queryFn: () => ResourceService.getResourceTagsForResource(resourceId),
      staleTime: ResourceStaleTimeEnum.GetResourceTags,
    })),
    combine: combineTags,
  });
